/**
 * Team handicap display rules. Mirrors the Flutter app's `handicaps.dart` — change both
 * together. All LABELS come from the server's rendered option lists on the competition's
 * handicap settings; this file only looks them up, so the two clients cannot disagree.
 */
export function useHandicaps() {
  // A roster row's readable name: the API's derived team name, else the players joined, else
  // a lone dragged-in player (raw player object until the roster is saved).
  const entrantName = (row) => {
    if (!row) return '';
    if (row.name) return row.name;
    if (row.derivedTeamName) return row.derivedTeamName;
    const players = row.players ?? [];
    if (players.length) return players.map((p) => p.fullName ?? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim()).join(' + ');
    if (row.fullName) return row.fullName;
    if (row.firstName || row.lastName) return `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
    return 'Unnamed';
  };
  /** A competition's settings object means handicaps are ON (a null/absent one means off). */
  const handicapsEnabled = (settings) => settings != null && settings.id != null;

  /** The exact label the server rendered for a stored value ("+20", "Rec 15.3", "Scratch", "6 shots"). */
  const labelFor = (settings, value) => {
    if (!handicapsEnabled(settings) || value == null) return null;
    const options = settings.scheme?.options ?? [];
    const hit = options.find((o) => Number(o.value) === Number(value));
    return hit?.label ?? String(value);
  };

  /** Chip text: the label, or "Not set" for a team the admin hasn't seeded yet. */
  const chipFor = (settings, value) => labelFor(settings, value) ?? 'Not set';

  /** Chip colour for a VALUE: +1 (weaker, playing off a start) ok, −1 (stronger, owing) warn, 0 (scratch) brand.
   *  `sign` is the server's direction-neutral HandicapSideView/PlayerView.sign; pass a raw value + settings to derive it. */
  const valueTone = (sign) => (sign > 0 ? 'tl-chip--ok' : sign < 0 ? 'tl-chip--warn' : 'tl-chip--brand');
  /** THE chip class for a stored value — every handicap chip in the app uses this, nothing else:
   *  receiving a start = ok (green), owing = warn (amber), scratch = brand, unset = plain. */
  const chipClassFor = (settings, value) => {
    if (value == null) return '';
    // a rating-style scale (bowls: higher = stronger) has no owe/receive — a value on its own is neither
    // easier nor harder, only the difference between two players is, so chips stay neutral
    const higherIsStronger = (settings?.directionOverride ?? settings?.scheme?.direction) === 1;
    return higherIsStronger ? 'tl-chip--brand' : valueTone(weakerSign(settings, value));
  };
  const weakerSign = (settings, value) => {
    const sign = Math.sign(Number(value ?? 0));
    const higherIsStronger = (settings?.directionOverride ?? settings?.scheme?.direction) === 1;
    return higherIsStronger ? -sign : sign;
  };

  /** Colour for a CHANGE, same meaning as the value chips: the move made things easier for the
   *  entrant (more start) = ok, harder (less start / more owed) = warn. Direction-aware. */
  const changeTone = (settings, oldValue, newValue) => {
    if (oldValue == null || newValue == null || Number(oldValue) === Number(newValue)) return 'tl-chip--brand';
    const higherIsStronger = (settings?.directionOverride ?? settings?.scheme?.direction) === 1;
    const up = Number(newValue) > Number(oldValue);
    const easier = up !== higherIsStronger;
    return easier ? 'tl-chip--ok' : 'tl-chip--warn';
  };

  const AUTO_MODES = [
    {value: 0, title: 'Off: admins change handicaps by hand'},
    {value: 1, title: 'Stepped: a fixed step per result (close / normal / decisive)'},
    {value: 2, title: 'Proportional: the bigger the margin, the bigger the move'},
  ];
  // Tournaments only: when the automatic moves land
  const AUTO_TIMINGS = [
    {value: 0, title: 'After every match'},
    {value: 1, title: 'When each stage completes (fixed through the groups, re-rated before the knockout)'},
  ];
  // Tennis / padel only: what the handicap does in a tie-break
  // Converted is the default — it is how most handicap tournaments run; Level is the opt-in club rule.
  const TIEBREAK_MODES = [
    {value: 1, title: 'Converted: the handicap becomes a points start scaled to the tie-break (Owe 30 starts a 7-pointer at −4, a match tie-break to 10 at −5)'},
    {value: 0, title: 'Level: tie-breaks are played off scratch (the traditional club rule)'},
  ];
  const DEFAULT_TIEBREAK_MODE = 1;
  const autoModeLabel = (mode) => ({0: 'Off', 1: 'Stepped', 2: 'Proportional'})[mode] ?? 'Off';

  const REASONS = {0: 'Initial', 1: 'Admin', 2: 'Automatic', 3: 'Carried forward', 4: 'Reverted', 5: 'Reset'};
  const reasonLabel = (reason) => REASONS[reason] ?? 'Change';

  /** Which pace preset the three saved steps correspond to, or 'custom'. */
  const paceFor = (settings) => {
    const presets = settings?.scheme?.pacePresets ?? {};
    const steps = [settings?.autoStepClose, settings?.autoStepNormal, settings?.autoStepDecisive].map(Number);
    for (const [name, values] of Object.entries(presets)) {
      if (values.length === 3 && values.every((v, i) => Number(v) === steps[i])) return name;
    }
    return 'custom';
  };

  return {
    entrantName,
    handicapsEnabled, labelFor, chipFor, chipClassFor, changeTone, valueTone, weakerSign, AUTO_MODES, AUTO_TIMINGS, TIEBREAK_MODES, DEFAULT_TIEBREAK_MODE, autoModeLabel, reasonLabel, paceFor};
}
